import React, {
  FC,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { FormikProps } from "formik";
import { t } from "i18next";
import { Exercise } from "@/app/exercises/exercises";
import { TrainingCategory } from "@/app/training/training-category";
import { ExerciseType } from "@/app/training/exercise-type";
import {
  equipmentOptions,
  exerciseTypeOptions,
  functionalExerciseOptions,
  muscleOptions,
} from "@/app/training/training";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { Select } from "@/shared/components/atoms/Select";
import { GripVertical } from "lucide-react";
import { useDraggable } from "@dnd-kit/core"; // Solo usamos useDraggable
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  faDumbbell,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Option } from "@/shared/utils/options";

type SideBarProps = {
  exercises: Array<Exercise>;
  onAddRoutine: (exercise: Exercise, category: TrainingCategory) => void;
  isLoading: boolean;
  formik: FormikProps<any>;
  currentPage: number;
  onPageChange: (page: number) => void;
  resetExercises: () => void;
  isDragging?: boolean; // Añadimos esta prop para sincronizar con el padre
};

const DraggableExerciseItem: FC<{
  exercise: Exercise;
  index: number;
  options?: Array<Option>;
  onExerciseClick?: (exercise: Exercise) => void;
}> = ({ exercise, index, options, onExerciseClick }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: exercise.id || `${index}`,
      data: { exercise, type: "EXERCISE" },
    });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.5 : 1,
    position: isDragging ? "absolute" : "relative",
    zIndex: isDragging ? 100 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className="flex flex-col items-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200 w-[220px] h-[200px] cursor-grab"
        onClick={(e) => {
          if (!isDragging && onExerciseClick) onExerciseClick(exercise);
        }}
      >
        <div className="flex items-center w-full">
          <div {...listeners} {...attributes} className="cursor-grab">
            <GripVertical size={18} className="text-gray-500 mr-3" />
          </div>
          <img
            className="h-24 w-24 rounded-md object-cover bg-gray-200"
            loading="lazy"
            src={exercise?.personalized_img ?? exercise.imageUrl}
            alt={exercise.exercise}
          />
          {options && options.length > 0 && (
            <div className="ml-auto">
              <Menu as="div" className="relative">
                {({ open }) => (
                  <>
                    <MenuButton
                      as="button"
                      className="text-gray-500 hover:text-gray-900 focus:outline-none p-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FontAwesomeIcon
                        icon={faEllipsisVertical}
                        className="h-5 w-5"
                      />
                    </MenuButton>
                    <MenuItems className="absolute right-0 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-gray-900/5 z-50 focus:outline-none">
                      {options.map((option, idx) => (
                        <MenuItem key={idx}>
                          {({ active }) => (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                option?.onClick && option.onClick(exercise);
                              }}
                              className={`block w-full text-left px-4 py-2 text-sm text-gray-700 ${active ? "bg-gray-100" : ""}`}
                            >
                              {option.element}
                            </button>
                          )}
                        </MenuItem>
                      ))}
                    </MenuItems>
                  </>
                )}
              </Menu>
            </div>
          )}
        </div>
        <p className="mt-2 text-sm font-medium text-gray-700 text-center truncate w-full">
          {exercise.exercise}
        </p>
      </div>
    </div>
  );
};

export const SideBarTraining: FC<SideBarProps> = React.memo(
  ({
    exercises,
    isLoading,
    onAddRoutine,
    formik,
    onPageChange,
    currentPage,
    resetExercises,
    isDragging,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [activeExercise, setActiveExercise] = useState<Exercise | null>(null); // Mantenemos este estado para uso interno si es necesario

    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => {
      if (!isDragging) setIsHovered(false);
    }, [isDragging]);

    const memoizedExercises = useMemo(() => exercises, [exercises]);

    const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        if (isLoading || isLoadingMore || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

        if (isNearBottom) {
          setIsLoadingMore(true);
          onPageChange(currentPage + 1);
        }
      },
      [currentPage, onPageChange, isLoading, isLoadingMore, hasMore]
    );

    const handleFilterChange = useCallback(
      (e: React.ChangeEvent<any>) => {
        formik.handleChange(e);
        resetExercises();
        setHasMore(true);
        onPageChange(1);
      },
      [formik, onPageChange, resetExercises]
    );

    useEffect(() => {
      if (!isLoading && isLoadingMore) {
        setIsLoadingMore(false);
        if (exercises.length === memoizedExercises.length && currentPage > 1) {
          setHasMore(false);
        }
      }
    }, [
      exercises,
      isLoading,
      isLoadingMore,
      currentPage,
      memoizedExercises.length,
    ]);

    return (
      <div
        className="relative w-full h-full"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="mt-80 fixed text-center px-1" style={{ width: "8.3%" }}>
          <div className="w-full rounded-lg mt-5 py-5 border-2 border-dashed border-gray-300 text-center">
            <FontAwesomeIcon
              icon={faDumbbell}
              className="mx-auto h-24 w-12 text-gray-400"
            />
            <span className="mt-2 block text-sm font-semibold text-gray-900">
              {t("user.views.add_excersise")}
            </span>
          </div>
        </div>

        <div
          className={`fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl z-10 border-l border-gray-200 transition-all duration-150 ease-in-out transform ${
            isHovered
              ? "translate-x-0 opacity-100"
              : "translate-x-full opacity-100"
          }`}
        >
          <div className="flex flex-col h-full p-6">
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900">
                Buscar Ejercicios
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <Select
                  className="w-full"
                  name="type"
                  optional
                  options={exerciseTypeOptions}
                  value={formik.values.type}
                  onChange={handleFilterChange}
                  label={t("accounting.views.type")}
                />
                <Select
                  className="w-full"
                  hidden={formik.values.type !== ExerciseType.Functional}
                  optional
                  name="functional"
                  options={functionalExerciseOptions}
                  value={formik.values.functional}
                  onChange={handleFilterChange}
                  label={t("user.views.functionals")}
                />
                <Select
                  className="w-full"
                  hidden={formik.values.type !== ExerciseType.Strength}
                  optional
                  name="muscle"
                  value={formik.values.muscle}
                  onChange={handleFilterChange}
                  options={muscleOptions}
                  label={t("user.views.muscles")}
                />
                <Select
                  className="w-full"
                  name="equipment"
                  onChange={handleFilterChange}
                  value={formik.values.equipment}
                  options={equipmentOptions}
                  label={t("user.views.type_equipment")}
                />
              </div>
              <InputWithCornerHint
                name="search"
                onChange={handleFilterChange}
                onBlur={formik.handleBlur}
                value={formik.values.search}
                label="Buscar"
                placeholder="Escribe el nombre del ejercicio..."
                type="text"
                className="w-full"
              />
            </div>

            <div
              ref={scrollRef}
              className={`flex-1 mt-6 ${isDragging ? "overflow-y-visible" : "overflow-y-scroll"} `}
              onScroll={handleScroll}
            >
              <div className="grid grid-cols-2 gap-4">
                {memoizedExercises?.map((exercise, index) => (
                  <DraggableExerciseItem
                    key={exercise?.id || `${index}`}
                    exercise={exercise}
                    index={index}
                    options={[
                      {
                        element: "Calentamiento",
                        onClick: (ex) =>
                          onAddRoutine(ex, TrainingCategory.Warm),
                      },
                      {
                        element: "Principal",
                        onClick: (ex) =>
                          onAddRoutine(ex, TrainingCategory.Main),
                      },
                      {
                        element: "Vuelta a la calma",
                        onClick: (ex) =>
                          onAddRoutine(ex, TrainingCategory.Stretching),
                      },
                    ]}
                  />
                ))}
              </div>
              {/*      {(isLoading || isLoadingMore) && (
                <div className="flex justify-center items-center py-6">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500"></div>
                  <span className="ml-3 text-gray-600 font-medium">
                    Cargando más ejercicios...
                  </span>
                </div>
              )} */}

              {(isLoading || isLoadingMore) && (
                <div className="grid grid-cols-2 gap-4 py-6">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={index}
                      className="w-[220px] h-[200px] bg-gray-200 animate-pulse rounded-lg"
                    >
                      <div className="flex p-4">
                        <div className="w-5 h-5 bg-gray-300 rounded mr-3" />
                        <div className="w-24 h-24 bg-gray-300 rounded-md" />
                      </div>
                      <div className="w-3/4 h-4 bg-gray-300 mx-auto mt-2 rounded" />
                    </div>
                  ))}
                </div>
              )}
              {!hasMore && memoizedExercises.length > 0 && !isLoading && (
                <div className="flex justify-center items-center py-6">
                  <span className="text-gray-600 font-medium">
                    No hay más ejercicios para mostrar
                  </span>
                </div>
              )}
              {memoizedExercises.length === 0 && !isLoading && (
                <div className="flex justify-center items-center py-6">
                  <span className="text-gray-600 font-medium">
                    No se encontraron ejercicios
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);
